import { createContext, useContext } from 'react';
import type { tWalletContext } from './Wallet.types';

/**
 * Default for consumers rendered ABOVE WalletProvider. `unknown` rather than
 * `disabled` on purpose: a consumer outside the provider has not learned that
 * the wallet is off, it simply cannot know. A future send gate must treat
 * `unknown` as "do not decide", never as "no money".
 */
const DEFAULT_WALLET_CONTEXT: tWalletContext = {
  status: 'unknown',
  balance: null,
  isLoadingBalance: false,
  balanceError: null,
  refetchBalance: async () => null,
  // Mismo criterio que `refetchBalance`, y aquí importa más: esto lo llama la
  // tarjeta al MONTARSE, así que un throw sobre un montaje accidental por
  // encima del provider sería una pantalla en blanco en lugar de una cifra sin
  // revalidar. No hay nada que releer fuera del provider, y no saberlo no es lo
  // mismo que fallar.
  revalidateBalance: async () => undefined,
  // Same reason as `refetchBalance`: a consumer above the provider has no
  // balance to settle, so the wait is a no-op rather than a throw. Nothing
  // here can start it, and nothing here would observe it.
  settleBalanceAfterCharge: () => undefined,
  // Mismo criterio, y por el mismo motivo que `status: 'unknown'`: un consumidor
  // fuera del provider no sabe qué hay que releer, y no saber no es lo mismo que
  // decidir que no hay nada. Inerte, nunca un throw — el modal se monta en cinco
  // lugares y uno futuro podría quedar arriba del provider por accidente; que
  // eso no repinte nada es un defecto visible, que reviente es una pantalla en
  // blanco.
  applyOutcome: () => undefined,
  autoTopUpToken: 0,
  // Nadie fuera del provider puede estar en una espera, así que `idle` no
  // inventa nada aquí — a diferencia de `status`, donde `disabled` sí habría
  // afirmado que la wallet está apagada sin haberlo aprendido.
  settlement: { kind: 'idle' },
  // RET-2283: fuera del provider no hay lectura del cupo, y no saber NO es
  // «cupo en cero» — el sello lo lee como «no puedo afirmar» y dice «Con costo».
  freeTierCounter: null,
  // RET-2327 (C-1): sin provider no hay lectura de saldo que fechar.
  balanceReadStartedAt: null,
  // RET-2319 · vuelta 3: sin contador no hay estampa, y el invariante lo exige.
  // Fuera del provider nunca hubo lectura, así que no hay edad que declarar —
  // `null` no es «vieja», es «ninguna». Quien afirme sobre el contador lo lee
  // como «no puedo juzgar la vejez» y, ante la duda, no promete.
  freeTierCounterReadAt: null,
  // RET-2319: `unknown`, no `waiting`. Es el mismo razonamiento que ya está
  // escrito arriba para `status: 'unknown'` — un consumidor fuera del provider
  // no es que esté esperando, es que NUNCA va a aprender. Una espera haría
  // callar al sello para siempre; un unknown durable lo deja decir «Con
  // costo», que es lo correcto cuando no hay forma de saber.
  freeTierCounterStatus: 'unknown',
  // RET-2319: `false`, no `true`. Igual que `status: 'unknown'` arriba, un
  // consumidor fuera del provider no está «cargando» — no hay ninguna
  // lectura en vuelo de la que pueda formar parte. Es un unknown DURABLE: si
  // esto fuera `true`, el sello callaría para siempre en vez de decir «Con
  // costo», y el silencio permanente esconde un costo real.
  isLoadingFreeTierCounter: false,
};

export const WalletContext = createContext<tWalletContext>(
  DEFAULT_WALLET_CONTEXT,
);

export function useWalletContext(): tWalletContext {
  return useContext(WalletContext);
}
